<!--
  ValetBandTable — listino valet a PACCHETTI, per fascia km (Fase 5).

  Ogni fascia ha il prezzo Simba di UN pickup, in tre scaglioni di volume:
  uno, due, tre o più. Il pacchetto costa `prezzo unitario × numero di pickup`
  e i mesi decidono solo la scadenza, non il prezzo (i campi si chiamano
  ancora `annual_*` per ragioni storiche).
  È un componente a parte e non una variante di BandTable perché le
  colonne non sono «tariffa / 2 operatori / quota partner» ma tre prezzi
  dello stesso tipo.

  v-model: array di righe
    { id?, min_km, max_km, p1, p2, p3, km, pp1?, pp2?, pp3?, pkm? }
  - `max_km` obbligatorio: il listino valet non ha fasce senza limite (i km
    oltre il pacchetto si pagano al €/km). Una fascia aperta salvata in
    passato mostra il campo vuoto, da compilare per chiuderla;
  - `p1..p3` = prezzi Simba (modificabili);
  - `km` = €/km Simba dei km oltre quelli compresi nel pacchetto (facoltativo:
    senza, i km in più non si addebitano);
  - `pp1..pp3`, `pkm` = prezzi del venditore, li imposta il partner dal suo
    gestionale: qui si mostrano soltanto.
-->
<template>
  <div class="vbt">
    <div class="vbt__scroll">
      <table class="vbt__tab">
        <thead>
          <tr>
            <th>Fascia km</th>
            <th
              v-for="n in OPTIONS"
              :key="n"
              class="vbt__num"
            >
              {{ n === 3 ? '3 o più pickup' : `${n} pickup` }}
              <span class="vbt__unit">€ per pickup</span>
            </th>
            <th class="vbt__num">
              Km oltre il pacchetto
              <span class="vbt__unit">€ al km in più</span>
            </th>
            <th class="vbt__act" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(b, i) in rows"
            :key="b.id || `new-${i}`"
          >
            <td>
              <div class="vbt__kmpair">
                <el-input
                  v-model.number="b.min_km"
                  size="small"
                  type="number"
                  min="0"
                  class="vbt__km"
                  :aria-label="`Inizio fascia ${i + 1}, km`"
                  @change="emitRows"
                />
                <span class="vbt__sep">–</span>
                <el-input
                  v-model.number="b.max_km"
                  size="small"
                  type="number"
                  min="0"
                  class="vbt__km"
                  :class="{ 'is-missing': b.max_km === null || b.max_km === '' }"
                  placeholder="fine"
                  :aria-label="`Fine fascia ${i + 1}, km`"
                  @change="emitRows"
                />
                <span class="vbt__unit">km</span>
              </div>
            </td>

            <td
              v-for="n in OPTIONS"
              :key="n"
              class="vbt__num"
            >
              <el-input
                v-model.number="b[`p${n}`]"
                size="small"
                type="number"
                step="0.01"
                min="0"
                class="vbt__price"
                :aria-label="`Fascia ${i + 1}, ${n === 3 ? '3 o più pickup' : `${n} pickup`}, euro per pickup`"
                @change="emitRows"
              />
              <!-- Quanto costerebbe il pacchetto a quel numero di pickup:
                   è il conto che fa il venditore, e a colpo d'occhio dice se
                   lo scaglione conviene. -->
              <div class="vbt__month">
                {{ totalePacchetto(b[`p${n}`], n) }}
              </div>
              <div
                v-if="showPartner && hasValue(b[`pp${n}`])"
                class="vbt__partner"
              >
                venditore {{ euro(b[`pp${n}`]) }} a pickup
              </div>
            </td>

            <td class="vbt__num">
              <el-input
                v-model.number="b.km"
                size="small"
                type="number"
                step="0.01"
                min="0"
                class="vbt__price vbt__price--km"
                placeholder="non addebitati"
                :aria-label="`Fascia ${i + 1}, euro al km oltre il pacchetto`"
                @change="emitRows"
              />
              <div
                v-if="showPartner && hasValue(b.pkm)"
                class="vbt__partner"
              >
                venditore {{ euro(b.pkm) }}/km
              </div>
            </td>

            <td class="vbt__act">
              <el-button
                type="danger"
                plain
                size="small"
                :aria-label="`Togli la fascia ${i + 1}`"
                @click="removeRow(i)"
              >
                ×
              </el-button>
            </td>
          </tr>

          <tr v-if="!rows.length">
            <td
              :colspan="OPTIONS.length + 3"
              class="vbt__empty"
            >
              Nessuna fascia impostata.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="vbt__actions">
      <el-button
        size="small"
        @click="addRow"
      >
        ＋ Aggiungi fascia
      </el-button>
    </div>

    <!-- Costo al km dei km che sforano il pacchetto. Si salva per fascia
         (`excess_price_per_km`), ma di solito è uno solo: da qui lo si mette
         su tutte le fasce in un colpo, poi si può ritoccare riga per riga. -->
    <div
      v-if="rows.length"
      class="vbt__bulk"
    >
      <span class="vbt__bulkLbl">Km oltre il pacchetto, stesso prezzo per tutte le fasce</span>
      <el-input
        v-model="bulkKm"
        size="small"
        type="number"
        step="0.01"
        min="0"
        class="vbt__price"
        aria-label="Euro al km in più, per tutte le fasce"
      >
        <template #append>
          €/km
        </template>
      </el-input>
      <el-button
        size="small"
        :disabled="!canApplyBulk"
        @click="applyBulkKm"
      >
        Applica a tutte
      </el-button>
    </div>
    <p class="vbt__hint">
      Una corsa col valet paga, tratta per tratta, solo i km oltre quelli
      compresi nel pacchetto. Senza prezzo al km quei km non si addebitano.
    </p>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

const OPTIONS = [1, 2, 3];

const hasValue = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
const euro = (v) => `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default {
  name: 'ValetBandTable',

  props: {
    modelValue: { type: Array, default: () => [] },
    // Mostra sotto ogni prezzo quello del venditore, se il partner l'ha impostato.
    showPartner: { type: Boolean, default: false },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const rows = computed(() => props.modelValue);

    const emitRows = () => emit('update:modelValue', [...rows.value]);

    const addRow = () => {
      // la fascia nuova riparte da dove finisce l'ultima
      const closed = rows.value.filter((b) => b.max_km !== null && b.max_km !== '');
      const last = closed[closed.length - 1];
      const from = last ? Number(last.max_km) : 0;
      emit('update:modelValue', [
        ...rows.value,
        // il €/km dei km in più di solito è uguale per tutte: si riprende dall'ultima
        {
          min_km: from,
          max_km: from + 50,
          p1: null,
          p2: null,
          p3: null,
          km: rows.value.length ? rows.value[rows.value.length - 1].km ?? null : null,
        },
      ]);
    };

    const removeRow = (i) => {
      const next = [...rows.value];
      next.splice(i, 1);
      emit('update:modelValue', next);
    };

    // Il prezzo del pacchetto a quello scaglione: unitario × numero di pickup.
    const totalePacchetto = (v, n) => (hasValue(v)
      ? `= ${euro(Math.round(Number(v) * n * 100) / 100)} per ${n}${n === 3 ? '+' : ''}`
      : '');

    const bulkKm = ref('');
    const canApplyBulk = computed(() => hasValue(bulkKm.value) && Number(bulkKm.value) >= 0);
    const applyBulkKm = () => {
      if (!canApplyBulk.value) return;
      const km = Math.round(Number(bulkKm.value) * 100) / 100;
      emit('update:modelValue', rows.value.map((b) => ({ ...b, km })));
    };

    return {
      OPTIONS, rows, emitRows, addRow, removeRow, totalePacchetto, hasValue, euro,
      bulkKm, canApplyBulk, applyBulkKm,
    };
  },
};
</script>

<style scoped>
.vbt__scroll { overflow-x: auto; }
.vbt__tab { width: 100%; border-collapse: collapse; min-width: 640px; }
.vbt__tab th {
  text-align: left; font-size: 10.5px; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--text-tertiary); font-weight: 700;
  padding: 6px 8px; vertical-align: bottom;
}
.vbt__tab th .vbt__unit {
  display: block; text-transform: none; letter-spacing: 0; font-weight: 500; margin-top: 2px;
}
.vbt__tab td { padding: 5px 8px; vertical-align: top; }
.vbt__num { text-align: right; }
.vbt__act { width: 48px; text-align: right; }

.vbt__kmpair { display: flex; align-items: center; gap: 6px; }
.vbt__km { width: 76px; }
.vbt__price { width: 104px; }
.vbt__price--km { width: 118px; }
.vbt__sep { color: var(--text-tertiary); }
.vbt__km.is-missing :deep(.el-input__wrapper) { box-shadow: 0 0 0 1px var(--state-attn) inset; }
.vbt__unit { font-size: 11.5px; color: var(--text-tertiary); white-space: nowrap; }
.vbt__month {
  margin-top: 3px; font-size: 11.5px; color: var(--text-tertiary); font-variant-numeric: tabular-nums;
}
.vbt__partner {
  margin-top: 2px; font-size: 11.5px; color: var(--brand-violet); font-variant-numeric: tabular-nums;
}
.vbt__empty { font-size: 12.5px; color: var(--text-tertiary); padding: 10px 8px !important; }
.vbt__actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.vbt__bulk {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--border-color);
}
.vbt__bulk .vbt__price { width: 150px; }
.vbt__bulkLbl { font-size: 12.5px; color: var(--text-secondary); }
.vbt__hint { margin: 6px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary); }
</style>
